import React, { useEffect, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Box, LoadError, Loading, VOLUME_LABELS, eur, eurUnit, num } from './costsFormat';

/**
 * Por canal: el coste y el coste por resultado, siempre al lado del coste por
 * intento. Una marcación barata no dice nada si casi ninguna llega a persona.
 */
const Volume = ({ volume }) => {
  const entries = Object.entries(volume || {});
  if (!entries.length) return <span className='text-slate-400'>—</span>;
  return (
    <div className='text-xs text-slate-600 space-y-0.5'>
      {entries.map(([k, v]) => (
        <div key={k}>
          {num(v)} {VOLUME_LABELS[k] || k}
        </div>
      ))}
    </div>
  );
};

const Row = ({ c }) => {
  const [open, setOpen] = useState(false);
  const canOpen = c.companies.length > 0;
  return (
    <>
      <tr className='border-t border-slate-100 align-top'>
        <td className='px-3 py-2'>
          {canOpen ? (
            <button type='button' className='inline-flex items-center gap-1 text-left' onClick={() => setOpen(o => !o)} aria-expanded={open}>
              {open ? <ChevronDownIcon className='w-4 h-4' /> : <ChevronRightIcon className='w-4 h-4' />}
              {c.label}
            </button>
          ) : (
            <span className='pl-5'>{c.label}</span>
          )}
          {c.note && <div className='text-xs text-amber-700 mt-1 pl-5'>{c.note}</div>}
        </td>
        <td className='px-3 py-2 text-right whitespace-nowrap font-medium'>{eur(c.cost_eur)}</td>
        <td className='px-3 py-2'>
          <Volume volume={c.volume} />
        </td>
        <td className='px-3 py-2'>
          <div className='text-xs space-y-0.5'>
            {c.per.map(p => (
              <div key={p.label} className='flex justify-between gap-3'>
                <span className='text-slate-500'>{p.label}</span>
                <span className='whitespace-nowrap'>{eurUnit(p.value)}</span>
              </div>
            ))}
          </div>
        </td>
      </tr>
      {open &&
        c.companies.map(co => (
          <tr key={co.company_id || 'none'} className='bg-slate-50 text-xs'>
            <td className='px-3 py-1 pl-10 text-slate-600'>{co.name}</td>
            <td className='px-3 py-1 text-right'>{eur(co.amount_eur)}</td>
            <td colSpan={2} />
          </tr>
        ))}
    </>
  );
};

const CostsByChannel = ({ month }) => {
  const [state, setState] = useState({ status: 'loading', data: null });

  useEffect(() => {
    let live = true;
    setState({ status: 'loading', data: null });
    api
      .get('/costs/channels', { params: { month } })
      .then(({ data }) => live && setState({ status: 'ok', data: data.data }))
      .catch(() => live && setState({ status: 'error', data: null }));
    return () => {
      live = false;
    };
  }, [month]);

  if (state.status === 'loading') return <Loading />;
  if (state.status === 'error') return <LoadError what='los canales' />;

  return (
    <Box title='Coste por canal y por resultado'>
      <div className='overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead className='text-xs text-slate-500 text-left'>
            <tr>
              <th className='px-3 py-2 font-medium'>Canal</th>
              <th className='px-3 py-2 font-medium text-right'>Coste</th>
              <th className='px-3 py-2 font-medium'>Volumen</th>
              <th className='px-3 py-2 font-medium'>Coste unitario</th>
            </tr>
          </thead>
          <tbody>
            {state.data.channels.map(c => (
              <Row key={c.channel} c={c} />
            ))}
          </tbody>
        </table>
      </div>
      <p className='text-xs text-slate-500 mt-2'>
        Despliega un canal para ver su coste variable por empresa. En los canales de cuota fija (WhatsApp de recobro, email, números) el coste unitario es
        la cuota repartida entre lo enviado: baja cuanto más se usa.
      </p>
    </Box>
  );
};

export default CostsByChannel;
